import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppContext } from 'src/contexts/app.context';
import type { ResourceShort } from 'src/types/master_data/resource.type';
import { findActiveRootItemByPath } from 'src/utils/menuUtils';
import { showNotification } from '@martech-solution/utils/utils';

export interface SidebarItem {
  id: number | string;
  name?: string;
  label?: string;
  parentId?: number | string | null;
  sort?: number;
  icon?: string | React.ReactNode;
  isActive?: boolean;
  children?: SidebarItem[];
  isCollapsed?: boolean;
  onClick?: () => void;
  path?: string;
  hasPermission?: boolean;
}

export interface ModernSidebarProps {
  className?: string;
  isMobileOpen?: boolean;
}

export interface UseModernSidebarReturn {
  sidebarMenuItems: SidebarItem[];
  handleItemClick: (item: SidebarItem) => void;
  rootResource: ResourceShort | null;
}

export const useModernSidebar = (): UseModernSidebarReturn => {
  const location = useLocation();
  const navigate = useNavigate();
  const { rootResources, setRootResource, setCurrentResource } = useContext(AppContext);
  const [expandedItemId, setExpandedItemId] = useState<string | number | null>(null);
  // 1. Find rootResource (selected from adaptive menu) based on current URL
  const rootResource = useMemo(() => {
    if (!rootResources || rootResources.length === 0) return null;
    const result = findActiveRootItemByPath(rootResources, location.pathname);
    return result;
  }, [rootResources, location.pathname]);

  // Update setRootResource when rootResource changes
  useEffect(() => {
    if (rootResource) {
      setRootResource(rootResource as ResourceShort);
    }
  }, [rootResource, setRootResource]);

  // 2. Tạo function checkActiveLeaf nhận tham số resource (không có side effect)
  const checkActiveLeaf = useCallback(
    (resource?: ResourceShort) => {
      if (!resource) return false;
      return `${location.pathname}/`.startsWith(`${resource.path}/`);
    },
    [location.pathname]
  );
  // Find and set current resource based on active path
  useEffect(() => {
    if (!rootResource) return;

    const findActiveResource = (resources: ResourceShort[]): ResourceShort | null => {
      for (const resource of resources) {
        const isActive = checkActiveLeaf(resource);
        if (isActive && (!resource.children || resource.children.length === 0)) {
          return resource;
        }
        if (resource.children && resource.children.length > 0) {
          const activeChild = findActiveResource(resource.children);
          if (activeChild) return activeChild;
        }
      }
      return null;
    };

    const activeResource = findActiveResource(rootResource.children || []);
    if (activeResource) {
      setCurrentResource(activeResource);
    }
  }, [rootResource, checkActiveLeaf, setCurrentResource]);

  // 3. Build sidebar from rootResource.children recursively and compare with activeLeaf to assign isActive
  const sidebarMenuItems = useMemo(() => {
    if (!rootResource) return [];

    // Chỉ cần lấy children trực tiếp từ rootResource
    const childrenResources = rootResource.children || [];
    if (childrenResources.length === 0) return [];

    // Convert tree structure trực tiếp
    const convertTreeToSidebarItems = (resources: ResourceShort[]): SidebarItem[] => {
      return resources
        .map((resource) => {
          const hasChildren = resource.children && resource.children.length > 0;
          return {
            id: resource.id,
            name: resource.name,
            icon: resource.icon || 'HelpCircle',
            path: resource.path,
            parentId: resource.parentId,
            sort: resource.sort,
            hasPermission: resource.hasPermission,
            // Mark active: chỉ leaf items mới active
            isActive: !hasChildren && checkActiveLeaf(resource),
            isCollapsed: expandedItemId !== resource.id,
            // Nếu có children thì đệ quy convert, nếu không thì undefined
            children: hasChildren ? convertTreeToSidebarItems(resource.children!) : undefined
          };
        })
        .sort((a, b) => (a.sort || 0) - (b.sort || 0));
    };

    return convertTreeToSidebarItems(childrenResources);
  }, [rootResource, expandedItemId, checkActiveLeaf]);

  // Handle item click: only navigate for leaf items
  const handleItemClick = useCallback(
    (item: SidebarItem) => {
      const hasChildren = item.children && item.children.length > 0;

      if (hasChildren) {
        // Toggle expand/collapse for parent items
        setExpandedItemId((prev) => (prev === item.id ? null : item.id));
        return;
      }

      // Check permission
      if (item.hasPermission === false) {
        showNotification('Bạn không có quyền truy cập vào chức năng này!', 'warning');
        return;
      }

      // Execute custom onClick if exists
      if (item.onClick) {
        item.onClick();
      }

      // Navigate to path
      if (item.path) {
        navigate(item.path);
      }
    },
    [navigate]
  );

  return {
    sidebarMenuItems,
    handleItemClick,
    rootResource
  };
};
